import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  fetchWeHaveServedCompanies,
  fetchLandingStats,
} from "../../../../services/weHaveServed";

// ─── Marquee item ─────────────────────────────────────────────────────────────
const Pill = React.memo(({ company }) => {
  const [imgError, setImgError] = useState(false);

  return (
    <div className="whs-pill" title={company.company_name}>
      <span className="whs-pill-logo">
        {company.logo && !imgError ? (
          <img
            src={company.logo}
            alt={company.company_name}
            className="whs-pill-img"
            loading="lazy"
            decoding="async"
            onError={() => setImgError(true)}
          />
        ) : (
          <span className="whs-pill-initials">
            {company.company_name?.slice(0, 2).toUpperCase()}
          </span>
        )}
      </span>

      <span className="whs-pill-name">{company.company_name}</span>
    </div>
  );
});

Pill.displayName = "Pill";

// ─── Main Component ───────────────────────────────────────────────────────────
const WeHaveServed = () => {
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [companiesData, statsData] = await Promise.all([
          fetchWeHaveServedCompanies(),
          fetchLandingStats(),
        ]);
        setCompanies(companiesData);
        setStats(statsData);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Ensure the base list is wide enough to span a large screen for seamless looping
  const MIN_ITEMS_FOR_SEAMLESS = 12;
  let baseCompanies = [...companies];
  
  if (baseCompanies.length > 0 && baseCompanies.length < MIN_ITEMS_FOR_SEAMLESS) {
    const repeatCount = Math.ceil(MIN_ITEMS_FOR_SEAMLESS / baseCompanies.length);
    baseCompanies = Array(repeatCount).fill(baseCompanies).flat();
  }

  // Triplicate the base for the marquee animation (CSS translates by -33.333%)
  const row1 = [...baseCompanies, ...baseCompanies, ...baseCompanies];
  const row2 = [
    ...[...baseCompanies].reverse(),
    ...[...baseCompanies].reverse(),
    ...[...baseCompanies].reverse(),
  ];

  if (loading) return null; // or a skeleton — keep it silent

  return (
    <section className="whs-section">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <motion.div
        className="whs-header"
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.65, ease: "easeOut" }}
        viewport={{ once: false }}
      >
        <h2 className="whs-heading">
          We Have <span className="whs-gradient-word">Served</span>
        </h2>
        <p className="whs-sub">
          Leading e-commerce brands trust Gapalyze for&nbsp;
          <strong>revenue clarity</strong> and smarter growth.
        </p>
      </motion.div>

      {/* ── Marquee Row 1 — scrolls left ───────────────────────────────── */}
      <div className="whs-marquee-wrap">
        <div className="whs-fade whs-fade--left" />
        <div className="whs-fade whs-fade--right" />
        <div className="whs-track whs-track--fwd">
          {row1.map((c, i) => (
            <Pill key={`r1-${c.id}-${i}`} company={c} />
          ))}
        </div>
      </div>

      {/* ── Marquee Row 2 — scrolls right ──────────────────────────────── */}
      <div className="whs-marquee-wrap whs-marquee-wrap--second">
        <div className="whs-fade whs-fade--left" />
        <div className="whs-fade whs-fade--right" />
        <div className="whs-track whs-track--rev">
          {row2.map((c, i) => (
            <Pill key={`r2-${c.id}-${i}`} company={c} />
          ))}
        </div>
      </div>

      {/* ── Stats bar ───────────────────────────────────────────────────── */}
      {stats.length > 0 && (
        <motion.div
          className="whs-stats"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut", delay: 0.15 }}
          viewport={{ once: false }}
        >
          {stats.map((s, i) => {
            let displayValue = String(s.value);
            // Append the correct signs if they are not already present
            if (s.label === "Brands & Agencies" && !displayValue.includes("+")) displayValue = `${displayValue}+`;
            if (s.label === "Orders Processed" && !displayValue.includes("+")) displayValue = `${displayValue}+`;
            if (s.label === "Saved Time" && !displayValue.includes("hrs")) displayValue = `${displayValue}hrs`;
            if (s.label === "Growth %" && !displayValue.includes("%")) displayValue = `${displayValue}%`;
            if (s.label === "Ad Spend Managed") {
              if (!displayValue.includes("Cr+")) displayValue = `${displayValue}Cr+`;
              if (!displayValue.startsWith("₹")) displayValue = `₹${displayValue}`;
            }

            return (
              <React.Fragment key={i}>
                <div className="whs-stat">
                  <span className="whs-stat-val">{displayValue}</span>
                  <span className="whs-stat-lbl">{s.label}</span>
                </div>
                {i < stats.length - 1 && <div className="whs-stat-div" />}
              </React.Fragment>
            );
          })}
        </motion.div>
      )}

    </section>
  );
};

export default WeHaveServed;
